import React, { useState, useEffect } from 'react';
import { ImageOnCakeSettings } from '../../../types';
import { Input, Button } from '../../atoms';
import './ImageOnCakeSettingsForm.css';

interface ImageOnCakeSettingsFormProps {
  settings: ImageOnCakeSettings | null;
  onSubmit: (settings: Partial<ImageOnCakeSettings>) => void;
  onCancel: () => void;
  loading?: boolean;
}

const ImageOnCakeSettingsForm: React.FC<ImageOnCakeSettingsFormProps> = ({
  settings,
  onSubmit,
  onCancel,
  loading = false
}) => {
  const [formData, setFormData] = useState({
    price: 15.00,
    isActive: true
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (settings) {
      setFormData({
        price: settings.price,
        isActive: settings.isActive
      });
    }
  }, [settings]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (formData.price < 0) {
      newErrors.price = 'Price cannot be negative';
    }
    
    if (formData.price > 1000) {
      newErrors.price = 'Price cannot exceed €1000';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    onSubmit(formData);
  };

  const handleToggle = (field: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: !prev[field as keyof typeof prev]
    }));
  };

  return (
    <div className="image-on-cake-settings-form">
      <form onSubmit={handleSubmit} className="image-on-cake-settings-form__form">
        <div className="image-on-cake-settings-form__header">
          <h4>Edit Image on Cake Settings</h4>
          <p>Configure the additional price for adding an edible image to cakes.</p>
        </div>

        <div className="image-on-cake-settings-form__fields">
          <div className="image-on-cake-settings-form__field">
            <Input
              id="price"
              label="Additional Price (€)"
              type="number"
              value={formData.price}
              onChange={(e) => handleInputChange('price', parseFloat(e.target.value) || 0)}
              placeholder="15.00"
              min={0}
              max={1000}
              step={0.01}
              required
              error={errors.price}
              className="image-on-cake-settings-form__input"
            />
            <p className="image-on-cake-settings-form__help">
              This amount will be added to the base cake price when customers select the "Image on cake" option.
            </p>
          </div>

          <div className="image-on-cake-settings-form__field">
            <div className="image-on-cake-settings-form__toggle">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={() => handleToggle('isActive')}
                  className="toggle-input"
                />
                <span className="toggle-slider"></span>
                <span className="toggle-text">Enable Image on Cake Option</span>
              </label>
              <p className="image-on-cake-settings-form__help">
                When enabled, customers will see the option to add an image to their cake during checkout.
              </p>
            </div>
          </div>
        </div>

        <div className="image-on-cake-settings-form__preview">
          <h5>Price Preview</h5>
          <div className="image-on-cake-preview">
            <div className="image-on-cake-preview__example">
              <span>Base cake price: €50.00</span>
              <span>Image on cake: +€{formData.price.toFixed(2)}</span>
              <span className="image-on-cake-preview__total">
                Total: €{(50 + formData.price).toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        <div className="image-on-cake-settings-form__actions">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={loading}
            className="image-on-cake-settings-form__cancel"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={loading}
            className="image-on-cake-settings-form__save"
          >
            {loading ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ImageOnCakeSettingsForm;
